// src/components/common/SpeciesSpotlight.jsx
// #103 zero-inventory graceful degradation — homepage Species Spotlight.
// Content, not commerce: rotates deterministically by day-of-year through
// species that have BOTH a care guide and a cover image, linking into the
// care-guide library. Renders in every inventory tier (the info core must
// not disappear when animals are in stock); at zero inventory HomePage
// slots it into the zero-hero block. Fetch failure or an empty candidate
// list renders nothing — the page is byte-identical to today.
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getPublicSpecies } from '../../services/api';
import { createSpeciesSlug } from '../../utils/slugUtils';
import '../../styles/speciesSpotlight.css';
import { formatSpeciesName } from '../../utils/scientificName';

const dayOfYear = () => {
  const now = new Date();
  const jan0 = new Date(now.getFullYear(), 0, 0);
  return Math.floor((now - jan0) / 86400000);
};

const SpeciesSpotlight = ({ className = '' }) => {
  const [speciesList, setSpeciesList] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const results = await getPublicSpecies();
        if (!cancelled) setSpeciesList(results);
      } catch (err) {
        // Non-fatal: spotlight simply doesn't render.
        console.warn('[SpeciesSpotlight] species load failed:', err?.message || err);
        if (!cancelled) setSpeciesList([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  /* Deterministic pick: candidates sorted by slug (stable regardless of
     API row order), indexed by day-of-year — same species all day, a new
     one tomorrow. Deps (`speciesList`) are declared above this hook —
     project rule: the production minifier exposes TDZ that dev mode
     tolerates. */
  const spotlight = useMemo(() => {
    const candidates = speciesList
      .filter((sp) => sp?.care_guide_available
        && (sp.cover_display_url || sp.cover_thumb_url))
      .sort((a, b) => createSpeciesSlug(a).localeCompare(createSpeciesSlug(b)));
    if (candidates.length === 0) return null;
    return candidates[dayOfYear() % candidates.length];
  }, [speciesList]);

  if (!spotlight) return null;

  const sciName = formatSpeciesName(spotlight, { withLocality: false });
  const commonName = spotlight.common_names?.[0] || null;
  const imgUrl = spotlight.cover_display_url || spotlight.cover_thumb_url;

  return (
    <section
      className={`ff-species-spotlight${className ? ` ${className}` : ''}`}
      aria-labelledby="species-spotlight-title"
    >
      <img
        src={imgUrl}
        alt={commonName || sciName}
        loading="lazy"
        className="ff-species-spotlight-img"
      />
      <div className="ff-species-spotlight-body">
        <span className="ff-species-spotlight-eyebrow">Species Spotlight</span>
        <h2 id="species-spotlight-title" className="ff-species-spotlight-title">
          {commonName || sciName}
        </h2>
        <p className="ff-species-spotlight-sci">
          <em>{sciName}</em>
          {spotlight.locality && <>&nbsp;&ldquo;{spotlight.locality}&rdquo;</>}
        </p>
        <p className="ff-species-spotlight-teaser">
          Today&rsquo;s pick from our care-guide library &mdash; husbandry
          notes from our racks to yours.
        </p>
        <Link
          to={`/care-guides/${createSpeciesSlug(spotlight)}`}
          className="ff-btn ff-btn--secondary ff-species-spotlight-cta"
        >
          Read the care guide
        </Link>
      </div>
    </section>
  );
};

export default SpeciesSpotlight;
